import { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import debounce from 'lodash/debounce';
import findIndex from 'lodash/findIndex';
import {
  getApplicants,
  getBranchOffices,
  getChildCompanies,
  getCostsCenters,
} from '../../../services/general';

const buildInitialInvoiceOptions = (reservation) => {
  const sources = [
    { id: reservation.company_id, data: reservation.company, sourceField: 'company_id' },
    { id: reservation.outsourcing_id, data: reservation.outsourcing, sourceField: 'outsourcing_id' },
    { id: reservation.third_company_id, data: reservation.third_company, sourceField: 'third_company_id' },
  ];
  return sources
    .filter(source => source.id && source.data)
    .map(({ id, data, sourceField }) => ({ id, business_name: data.business_name, document_number: data.business_document_number || data.document_number, sourceField }));
};

export const useGeneralDataPE = ({ form, companies, setCompanies }) => {
  const [loading, setLoading] = useState(false);
  const [childCompanyLoading, setChildCompanyLoading] = useState(false);
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [applicantList, setApplicantList] = useState([]);
  const [isDebtor, setIsDebtor] = useState(false);
  const [branchOffices, setBranchOffices] = useState();
  const [applicantModal, setApplicantModal] = useState({ type: 'create', visible: false });
  const [childCompanyModal, setChildCompanyModal] = useState({ type: 'create', visible: false });
  const [costCenterModal, setCostCenterModal] = useState({ type: 'create', visible: false });
  const [costsCenters, setCostsCenters] = useState([]);
  const [childCompanies, setChildCompanies] = useState([]);
  const [showChildCompanies, setShowChildCompanies] = useState(false);
  const [invoiceCompanyOptions, setInvoiceCompanyOptions] = useState([]);

  const addApplicant = useCallback((applicant) => {
    const index = findIndex(applicantList, ['id', applicant.id]);
    if (index >= 0) {
      setApplicantList(prev => {
        const _list = [...prev];
        _list[index] = applicant;
        return _list;
      });
      return;
    }
    setApplicantList(oldArray => [...oldArray, applicant]);
  }, [applicantList]);

  const addCompany = useCallback((company) => {
    const index = findIndex(companies, ['id', company.id]);
    if (index >= 0) {
      setCompanies(prev => {
        const _list = [...prev];
        _list[index] = company;
        return _list;
      });
      return;
    }
    setCompanies(oldArray => [...oldArray, company]);
  }, [companies, setCompanies]);

  const addChildCompany = useCallback((childCompany) => {
    setChildCompanies(oldArray => [...oldArray, childCompany]);
  }, []);

  const addCostCenter = useCallback((costCenter) => {
    const index = findIndex(costsCenters, ['id', costCenter.id]);
    if (index >= 0) {
      setCostsCenters(prev => {
        const _list = [...prev];
        _list[index] = costCenter;
        return _list;
      });
      return;
    }
    setCostsCenters(oldArray => [...oldArray, costCenter]);
  }, [costsCenters]);

  const fetchApplicants = useMemo(() => debounce(async (query) => {
    setApplicantLoading(true);
    try {
      const response = query ? await getApplicants({ query, limit: 50 }) : { data: [] };
      setApplicantList(response.data);
    } catch (error) {
      console.error(`Error interno ${error}`);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: '45vh', fontSize: 16 }
      });
    } finally {
      setApplicantLoading(false);
    }
  }, 500), []);

  const fetchBranchOffices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBranchOffices();
      setBranchOffices(response);
    } catch (error) {
      console.error(`Error al cargar Sucursales: ${error}`);
      message.error({
        content: `Error al cargar Sucursales: ${error}`,
        style: { marginTop: '45vh', fontSize: 16 }
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCostsCenters = useCallback(async (companyId) => {
    const response = await getCostsCenters({ companyId, limit: 50 });
    setCostsCenters(response);
  }, []);

  const fetchChildCompanies = useCallback(async (name) => {
    setChildCompanyLoading(true);
    try {
      const companyId = form.getFieldValue('company_id');
      const response = await getChildCompanies({ companyId, limit: 50, name });
      setChildCompanies(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: '32vh', fontSize: 16 }
      });
    } finally {
      setChildCompanyLoading(false);
    }
  }, [form]);

  const searchChildCompanies = useMemo(() => debounce(async (name) => {
    fetchChildCompanies(name);
  }, 500), [fetchChildCompanies]);


  const buildUpdatedInvoiceOptions = (currentOptions, sourceField, company) => {
    const withoutCurrentField = currentOptions.filter(item => item.sourceField !== sourceField);
    return company
      ? [...withoutCurrentField, { id: company.id, business_name: company.business_name, document_number: company.document_number, sourceField }]
      : withoutCurrentField;
  };

  const clearInvoiceSelectionIfInvalid = useCallback((validOptions) => {
    const currentInvoiceId = form.getFieldValue('invoice_company_id');
    const isStillValid = validOptions.some(item => item.id === currentInvoiceId);
    if (currentInvoiceId && !isStillValid) {
      form.setFieldsValue({ invoice_company_id: undefined });
    }
  }, [form]);

  const updateInvoiceCompanyOption = useCallback((field, company) => {
    const sourceField = { company: 'company_id', outsourcing: 'outsourcing_id', thirdCompany: 'third_company_id' }[field];
    if (!sourceField) return;

    setInvoiceCompanyOptions(currentOptions => {
      const updatedOptions = buildUpdatedInvoiceOptions(currentOptions, sourceField, company);
      clearInvoiceSelectionIfInvalid(updatedOptions);
      return updatedOptions;
    });
  }, [clearInvoiceSelectionIfInvalid]);

  const initializeInvoiceOptions = useCallback((reservation) => {
    setInvoiceCompanyOptions(buildInitialInvoiceOptions(reservation));
  }, []);

  const handleOnChangeSearch = useCallback((field, value, option, setter) => {
    if (value) {
      form.setFieldsValue({ [field]: value });
    } else {
      form.setFieldsValue({ [field]: null });
      setter([]);
    }

    if (['company', 'outsourcing', 'thirdCompany'].includes(field)) {
      updateInvoiceCompanyOption(field, option?.company || null);
    }

    if (field === 'company') {
      if (option) {
        setIsDebtor(option.company.is_debtor);
        if (option.company.is_provider) {
          setShowChildCompanies(option.company.is_provider);
          fetchChildCompanies('');
        }
        if (option.company.has_cost_centers) {
          fetchCostsCenters(value);
        } else {
          setCostsCenters([]);
        }
      } else {
        setShowChildCompanies(false);
        setCostsCenters([]);
      }
    }
  }, [form, fetchChildCompanies, fetchCostsCenters, updateInvoiceCompanyOption]);

  return {
    loading,
    applicantLoading,
    childCompanyLoading,
    applicantList,
    isDebtor,
    setIsDebtor,
    branchOffices,
    applicantModal,
    setApplicantModal,
    childCompanyModal,
    setChildCompanyModal,
    costCenterModal,
    setCostCenterModal,
    costsCenters,
    setCostsCenters,
    childCompanies,
    setChildCompanies,
    showChildCompanies,
    setShowChildCompanies,
    addApplicant,
    addCompany,
    addChildCompany,
    addCostCenter,
    fetchApplicants,
    fetchBranchOffices,
    searchChildCompanies,
    handleOnChangeSearch,
    setApplicantList,
    invoiceCompanyOptions,
    initializeInvoiceOptions,
  };
};
